import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Retourne les dimensions naturelles d'une image (base64 ou URL).
 */
function getImageDimensions(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload  = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ w: 0, h: 0 });
    img.src = src;
  });
}

/**
 * Charge une image distante et retourne sa data-URL base64.
 */
async function loadImageBase64(url) {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload  = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
}

/**
 * Génère et télécharge la fiche de sécurité FFESSM en PDF.
 * @param {object} formData     - données du formulaire fiche
 * @param {object} session      - session courante (name, location, logo?)
 * @returns {Promise<void>}
 * @throws {Error} si la fiche est vide
 */
export async function generateFichePDF(formData, session) {
  if (!formData.palanquees || formData.palanquees.length === 0) {
    throw new Error('Ajoutez au moins une palanquée');
  }

  // Charger le logo Asterina (remplace bloc FFESSM gauche)
  const asterinaBase64 = await loadImageBase64('/asterina.png');
  // Logo de session (haut droite) — déjà en base64 s'il existe
  const sessionLogo = session?.logo || null;
  // Dimensions réelles du logo de session pour conserver les proportions
  const sessionLogoDims = sessionLogo ? await getImageDimensions(sessionLogo) : null;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const PW = 210, PH = 297, MG = 8, UW = PW - MG * 2;

  // ── Colors ──────────────────────────────────────────────────────────
  const HDR   = [18, 95, 168];  // légèrement plus clair qu'avant
  const MBLUE = [14, 100, 165];
  const YEL   = [255, 195, 0];
  const WHT   = [255, 255, 255];
  const DARK  = [22, 32, 52];
  const GLIGHT= [243, 247, 252];
  const BDR   = [175, 192, 215];
  const SBROW = [230, 242, 255];
  const LBROW = [210, 228, 248];
  const FTRBG = [0, 50, 110];
  const LEGBG = [232, 241, 253];

  const F = (c) => doc.setFillColor(c[0], c[1], c[2]);
  const D = (c) => doc.setDrawColor(c[0], c[1], c[2]);
  const T = (c) => doc.setTextColor(c[0], c[1], c[2]);
  const lw = (w) => doc.setLineWidth(w);

  let y = 0;
  let pageNum = 0;

  // ── Footer ───────────────────────────────────────────────────────────
  const drawFooter = () => {
    F(FTRBG); doc.rect(0, PH - 11, PW, 11, 'F');
    T(WHT); doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
    doc.text(`${session.name}   ·   Fiche de Sécurité   ·   ${formData.date}`, PW / 2, PH - 6.5, { align: 'center' });
    T([160, 195, 235]); doc.setFontSize(5.5); doc.setFont('helvetica', 'normal');
    doc.text(`Club Asterina  ·  ${new Date().toLocaleDateString('fr-FR')}`, PW / 2, PH - 2.5, { align: 'center' });
  };

  // ── Header ───────────────────────────────────────────────────────────
  const drawPageHeader = () => {
    // Blue band
    F(HDR); doc.rect(0, 0, PW, 27, 'F');
    F(YEL); doc.rect(0, 26.5, PW, 1.5, 'F');

    // ── Left block: Asterina logo (replaces FFESSM block) ────────────
    if (asterinaBase64) {
      // Place logo directly on the blue header, no background box
      doc.addImage(asterinaBase64, 'PNG', MG + 1, 2.5, 24, 22, undefined, 'FAST');
    } else {
      // Fallback text
      F(MBLUE); doc.setDrawColor(14, 100, 165); doc.rect(MG, 2, 26, 23, 'F');
      T(YEL); doc.setFontSize(9.5); doc.setFont('helvetica', 'bold');
      doc.text('ASTERINA', MG + 13, 9, { align: 'center' });
      T(WHT); doc.setFontSize(5.5); doc.setFont('helvetica', 'normal');
      doc.text('Planning', MG + 13, 14, { align: 'center' });
      doc.text('Plongée', MG + 13, 18, { align: 'center' });
    }

    // ── Center title ──────────────────────────────────────────────────
    T(WHT); doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text('FICHE DE SÉCURITÉ', PW / 2, 10, { align: 'center' });
    doc.setFontSize(9.5);
    doc.text('FEUILLE DE PALANQUÉE', PW / 2, 16.5, { align: 'center' });
    // Session name + location under subtitle
    if (session.name) {
      T([200, 220, 255]); doc.setFontSize(7.5); doc.setFont('helvetica', 'bold');
      doc.text(session.name, PW / 2, 21.5, { align: 'center' });
    }
    if (session.location) {
      T([160, 195, 235]); doc.setFontSize(6.5); doc.setFont('helvetica', 'normal');
      doc.text(session.location, PW / 2, 25.5, { align: 'center' });
    }

    // ── Right block: session logo ─────────────────────────────────────
    const rightBlockX = PW - MG - 27;
    if (sessionLogo && sessionLogoDims && sessionLogoDims.w > 0) {
      const maxW = 24, maxH = 22;
      const ratio = sessionLogoDims.w / sessionLogoDims.h;
      let imgW, imgH;
      if (ratio > maxW / maxH) {
        imgW = maxW;
        imgH = maxW / ratio;
      } else {
        imgH = maxH;
        imgW = maxH * ratio;
      }
      const imgX = rightBlockX + (maxW - imgW) / 2;
      const imgY = 2.5 + (maxH - imgH) / 2;
      doc.addImage(sessionLogo, 'PNG', imgX, imgY, imgW, imgH, undefined, 'FAST');
    }

    y = 29;

    // ── Info rows ─────────────────────────────────────────────────────
    const rowH = 6.5;
    D(BDR); lw(0.3);

    // Ligne 1 : Nb de plongeurs | Date | Heure (réduit) | Bateau | Pilote
    const nb1W = 28, dateW = 40, heureW = 22, bateauW = 46, piloteW = UW - nb1W - dateW - heureW - bateauW;
    [
      { label: 'Nb de plongeurs :', val: String(formData.nbPlongeurs || ''), w: nb1W },
      { label: 'Date :', val: formData.date, w: dateW },
      { label: 'Heure :', val: formData.heure, w: heureW },
      { label: 'Bateau :', val: formData.bateau, w: bateauW },
      { label: 'Pilote :', val: formData.pilote || '', w: piloteW },
    ].reduce((x, f) => {
      F(GLIGHT); doc.rect(x, y, f.w, rowH, 'FD');
      T([120, 135, 165]); doc.setFontSize(5.8); doc.setFont('helvetica', 'bold');
      doc.text(f.label, x + 1.5, y + 2.8);
      T(DARK); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      doc.text(f.val || '', x + doc.getTextWidth(f.label) + 3, y + 5.2);
      return x + f.w;
    }, MG);
    y += rowH;

    // Ligne 2 : Directeur de plongée | Directeur de stage | Site de plongée
    const dpW = 70, dsW = 60, siteW2 = UW - dpW - dsW;
    [
      { label: 'Directeur de Plongée :', val: formData.dp, w: dpW },
      { label: 'Directeur de stage :', val: formData.directeurStage, w: dsW },
      { label: 'Site de plongée :', val: formData.site, w: siteW2 },
    ].reduce((x, f) => {
      F(GLIGHT); doc.rect(x, y, f.w, rowH, 'FD');
      T([120, 135, 165]); doc.setFontSize(5.8); doc.setFont('helvetica', 'bold');
      doc.text(f.label, x + 1.5, y + 2.8);
      T(DARK); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      doc.text(f.val || '', x + doc.getTextWidth(f.label) + 3, y + 5.2);
      return x + f.w;
    }, MG);
    y += rowH;

    // Ligne 3 : Président du stage | Commentaires
    const presW = 80, commW = UW - presW;
    [
      { label: 'Président du stage :', val: formData.president, w: presW },
      { label: 'Commentaires :', val: formData.commentaires, w: commW },
    ].reduce((x, f) => {
      F(GLIGHT); doc.rect(x, y, f.w, rowH, 'FD');
      T([120, 135, 165]); doc.setFontSize(5.8); doc.setFont('helvetica', 'bold');
      doc.text(f.label, x + 1.5, y + 2.8);
      T(DARK); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      doc.text(f.val || '', x + doc.getTextWidth(f.label) + 3, y + 5.2);
      return x + f.w;
    }, MG);
    y += rowH;

    // ── Legend ────────────────────────────────────────────────────────
    F(LEGBG); D(BDR); lw(0.3);
    doc.rect(MG, y, UW, 13, 'FD');
    const legLines = [
      { bold: 'BREVET :',   normal: 'P1 / P2 / P3 / N1 / N2 / N3 / PN2 / PA20 / PA40 / PA60 / E1 / E2 / E3 / E4 / GP / MF1 / MF2' },
      { bold: 'APTITUDE :', normal: 'PA-12  /  PE-20  /  PA-20  /  PE-40  /  PA-40  /  PE-60  /  PA-60 / Bapt' },
      { bold: 'FONCTION :', normal: 'ENC = Encadrant Technique   |   GP = Guide de Palanquée   |   AUT = Autonome   |   M = Membre de la palanquée' },
    ];
    legLines.forEach((l, i) => {
      const ly = y + 4 + i * 4;
      T(HDR); doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
      doc.text(l.bold, MG + 2, ly);
      T([45, 60, 90]); doc.setFont('helvetica', 'normal');
      doc.text(l.normal, MG + 2 + doc.getTextWidth(l.bold) + 1.5, ly);
    });
    y += 14;
  };

  const newPage = () => {
    if (pageNum > 0) doc.addPage();
    pageNum++;
    drawPageHeader();
  };

  const drawPalanquee = (pal) => {
    const minRows = 4;
    const rows = Math.max(pal.plongeurs.length, minRows);
    const rowH = 6.5;
    const palH = 7.5 + 5.5 + rows * rowH + 6.5 + 5;

    if (y + palH + 12 > PH) { drawFooter(); newPage(); }

    const RPW = 68;
    const TW  = UW - RPW;
    const cNm = TW * 0.41;
    const cBr = TW * 0.14;
    const cAp = TW * 0.17;
    const cFn = TW * 0.12;
    const cGa = TW * 0.08;
    const cGn = TW * 0.08;

    F(HDR); doc.rect(MG, y, UW, 7.5, 'F');
    T(WHT); doc.setFontSize(9.5); doc.setFont('helvetica', 'bold');
    doc.text(`Palanquée N° ${pal.numero}`, MG + 3, y + 5.5);

    const techX = MG + 60;
    const expX  = techX + 36;
    D(WHT); lw(0.5);
    F(pal.type === 'technique' ? YEL : WHT); doc.rect(techX, y + 1.8, 4, 4, 'FD');
    if (pal.type === 'technique') { T(DARK); doc.setFontSize(8); doc.text('X', techX + 0.6, y + 5.3); }
    T(WHT); doc.setFontSize(8.5); doc.setFont('helvetica', 'normal');
    doc.text('Technique', techX + 5, y + 5.5);

    F(pal.type === 'exploration' ? YEL : WHT); doc.rect(expX, y + 1.8, 4, 4, 'FD');
    if (pal.type === 'exploration') { T(DARK); doc.setFontSize(8); doc.text('X', expX + 0.6, y + 5.3); }
    T(WHT); doc.setFontSize(8.5); doc.setFont('helvetica', 'normal');
    doc.text('Exploration', expX + 5, y + 5.5);

    doc.setFontSize(7.5);
    const brevPX = expX + 30;
    doc.text(`Brevet en Préparation : ${pal.brevetPreparation || ''}`, brevPX, y + 5.5, { maxWidth: UW - (brevPX - MG) - 26 });
    const checkW = 4.2;
    const checkX = MG + UW - checkW - 2.5;
    const labelX = checkX - 7.5;
    T(WHT); doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
    doc.text('Sortie', labelX, y + 5.5, { align: 'right' });
    D(DARK); lw(0.3); F(WHT);
    doc.rect(checkX, y + 2.1, checkW, checkW, 'FD');
    y += 7.5;

    const subH = 5.5;
    F(LBROW); D(BDR); lw(0.25);
    let cx = MG;
    [cNm, cBr, cAp, cFn, cGa, cGn].forEach(w => { doc.rect(cx, y, w, subH, 'FD'); cx += w; });
    doc.rect(MG + TW, y, RPW / 2, subH, 'FD');
    doc.rect(MG + TW + RPW / 2, y, RPW / 2, subH, 'FD');

    T([25, 60, 120]); doc.setFontSize(6); doc.setFont('helvetica', 'bold');
    cx = MG;
    [
      { w: cNm, label: 'Prénom Nom' }, { w: cBr, label: 'Brevet'   },
      { w: cAp, label: 'Aptitude'   }, { w: cFn, label: 'Fct'      },
      { w: cGa, label: 'Air'        }, { w: cGn, label: 'Nitrox'   },
    ].forEach(c => {
      doc.text(c.label, cx + c.w / 2, y + 3.8, { align: 'center' });
      cx += c.w;
    });
    doc.text('Prévu',   MG + TW + RPW / 4,     y + 3.8, { align: 'center' });
    doc.text('Réalisé', MG + TW + RPW * 3 / 4, y + 3.8, { align: 'center' });
    y += subH;

    for (let ri = 0; ri < rows; ri++) {
      const ry    = y + ri * rowH;
      const diver = pal.plongeurs[ri];

      F(ri % 2 === 0 ? WHT : [250, 253, 255]);
      D(BDR); lw(0.22);
      let rcx = MG;
      // eslint-disable-next-line no-loop-func
      [cNm, cBr, cAp, cFn, cGa, cGn].forEach(w => { doc.rect(rcx, ry, w, rowH, 'FD'); rcx += w; });

      if (diver) {
        T(DARK); doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
        doc.text(diver.name,          MG + 1.5,                      ry + rowH * 0.7);
        doc.text(diver.brevet  || '', MG + cNm + cBr / 2,            ry + rowH * 0.7, { align: 'center' });
        doc.text(diver.aptitude || '', MG + cNm + cBr + cAp / 2,     ry + rowH * 0.7, { align: 'center' });
        doc.text(diver.fonction || '', MG + cNm + cBr + cAp + cFn / 2, ry + rowH * 0.7, { align: 'center' });
        T([0, 100, 175]); doc.setFontSize(9); doc.setFont('helvetica', 'bold');
        const gAirX  = MG + cNm + cBr + cAp + cFn;
        const gNitrX = gAirX + cGa;
        if (pal.gaz === 'nitrox') {
          doc.text('X', gNitrX + cGn / 2, ry + rowH * 0.75, { align: 'center' });
        } else {
          doc.text('X', gAirX + cGa / 2,  ry + rowH * 0.75, { align: 'center' });
        }
      }
    }

    const totalH = rows * rowH;
    const sH     = totalH / 4;
    const labelW = 13;
    const halfW  = (RPW - labelW) / 2;
    const sections = [
      { label: 'Prof.',   prev: pal.profPrevu   ? `${pal.profPrevu} m`    : '', real: pal.profRealise   ? `${pal.profRealise} m`    : '' },
      { label: 'Tps Max', prev: pal.tpsMaxPrevu ? `${pal.tpsMaxPrevu} mn` : '', real: pal.tpsMaxRealise ? `${pal.tpsMaxRealise} mn` : '' },
      { label: 'Paliers', prev: pal.paliersPrevu ?? pal.paliers ?? '', real: pal.paliersRealise ?? '' },
      { label: 'HI',      prev: pal.hi || '',   real: pal.hs || '', subLabels: ['Entrée', 'Sortie'] },
    ];
    sections.forEach((s, si) => {
      const sY = y + si * sH;
      F(SBROW); D(BDR); lw(0.25);
      doc.rect(MG + TW, sY, RPW, sH, 'FD');
      D([160, 190, 220]); lw(0.2);
      // Divider between label col and value cols
      doc.line(MG + TW + labelW, sY, MG + TW + labelW, sY + sH);
      if (!s.full) {
        // Divider between Prévu and Réalisé
        doc.line(MG + TW + labelW + halfW, sY, MG + TW + labelW + halfW, sY + sH);
      }
      T([25, 65, 130]); doc.setFontSize(6); doc.setFont('helvetica', 'bold');
      doc.text(s.label, MG + TW + labelW / 2, sY + sH / 2 + 1.5, { align: 'center' });
      // Petites étiquettes en haut à gauche des cellules (ex: Entrée / Sortie pour HI)
      if (s.subLabels) {
        T([120, 155, 200]); doc.setFontSize(4.2); doc.setFont('helvetica', 'italic');
        doc.text(s.subLabels[0], MG + TW + labelW + 1.2, sY + 2.2);
        doc.text(s.subLabels[1], MG + TW + labelW + halfW + 1.2, sY + 2.2);
      }
      T(DARK); doc.setFontSize(s.full ? 7 : 8); doc.setFont('helvetica', s.full ? 'normal' : 'bold');
      if (s.full) {
        doc.text(s.prev, MG + TW + labelW + 2, sY + sH / 2 + 1.5, { maxWidth: RPW - labelW - 3 });
      } else {
        doc.text(s.prev, MG + TW + labelW + halfW / 2,        sY + sH / 2 + 2, { align: 'center' });
        doc.text(s.real, MG + TW + labelW + halfW + halfW / 2, sY + sH / 2 + 2, { align: 'center' });
      }
    });
    y += totalH;
    y += 5;
  };

  newPage();
  formData.palanquees.forEach(drawPalanquee);
  drawFooter();

  const bateauSlug = (formData.bateau || 'fiche').replace(/\s+/g, '-');
  const dateSlug   = (formData.date   || '').replace(/\//g, '-');
  doc.save(`fiche-securite-${(session.name || 'stage').replace(/\s+/g, '-')}-${dateSlug}-${bateauSlug}.pdf`);
}
